import { notEmpty } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import { useLiveArticleScrollContext } from "~/components/article/article-render/live-article/contexts";
import type { timelineLiveArticleLiveContent$key } from "~/queries/__generated__/timelineLiveArticleLiveContent.graphql";

import { Container, StyledLiveArticleTimelineItem } from "./styles";

type Props = {
  className?: string;
  deepLinkPosition?: number;
  reference: timelineLiveArticleLiveContent$key;
};

export const LiveArticleTimeline: FunctionComponent<Props> = ({
  className,
  deepLinkPosition = 0,
  reference,
}) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { setTimelineScrollContainerReference } = useLiveArticleScrollContext();

  const contents = useFragment(
    graphql`
      fragment timelineLiveArticleLiveContent on LiveContent @relay(plural: true) {
        customTime
        entityUuid
        ...itemLiveArticleTimelineLiveContent
      }
    `,
    reference,
  );

  const liveContents = useMemo(
    () =>
      contents?.filter(notEmpty)?.sort((a, b) => (b?.customTime ?? 0) - (a?.customTime ?? 0)) ?? [],
    [contents],
  );

  const renderLiveContents = useMemo(
    () => liveContents.slice(deepLinkPosition),
    [deepLinkPosition, liveContents],
  );

  const capture = useCallback(
    (element: HTMLDivElement) => {
      if (!element || !setTimelineScrollContainerReference) return;
      setTimelineScrollContainerReference(element);
    },
    [setTimelineScrollContainerReference],
  );

  return (
    <Container $stickyStackedHeight={appBarStickyHeight} className={className} ref={capture}>
      {renderLiveContents.map((liveContent, index) => (
        <StyledLiveArticleTimelineItem
          key={liveContent.entityUuid ?? index}
          reference={liveContent}
        />
      ))}
    </Container>
  );
};

LiveArticleTimeline.displayName = "LiveArticleTimeline";
